import { Button as MUIButton, ButtonProps, CircularProgress } from '@mui/material';
import { createRef, ReactNode, useEffect, useState } from 'react';
import theme, { lightenDarkenColor } from '../../App.theme';
import { MANAGE_BUTTON_WIDTHS } from 'featureFlags';

type ColorKey = keyof typeof theme.colours;

export type ButtonColors =
  | 'white on active'
  | 'active on white'
  | 'black on white'
  | 'white on red'
  | 'red on white'
  | 'white on primary'
  | 'primary on white'
  | 'white on amber'
  | 'white on GREY3C';

type Props = ButtonProps & {
  label?: ReactNode;
  colors?: ButtonColors;
  loading?: boolean;
  square?: boolean;
  pulsate?: boolean;
};

const Button = ({
  label,
  children: _children = label,
  colors = 'black on white',
  loading,
  square = false,
  pulsate = false,
  ref,
  ...buttonProps
}: Props) => {
  const buttonRef = ref || createRef<HTMLButtonElement>();

  const [buttonWidth, setButtonWidth] = useState<string>('auto');
  const [children, setChildren] = useState<ReactNode>(_children);

  // maintain button width when loading
  useEffect(() => {
    if (!MANAGE_BUTTON_WIDTHS) return;
    if (loading || 'function' == typeof buttonRef || !buttonRef?.current) return;
    const childrenChanged = children != _children;
    const buttonWidth = childrenChanged ? 'auto' : `${buttonRef.current.offsetWidth}px`;
    setButtonWidth(buttonWidth);
    if (childrenChanged) setChildren(_children);
  }, [buttonRef, children, _children, loading]);

  // always set button width to auto on page load to capture initial width
  useEffect(() => {
    if (!MANAGE_BUTTON_WIDTHS) return;
    setButtonWidth('auto');
  }, []);

  const tc = { ...theme.colors, ...theme.greys };
  const [fgKey, bgKey] = colors.split(' on ', 2) as [ColorKey, ColorKey];
  const color = tc[fgKey];
  const backgroundColor = buttonProps?.disabled ? theme.greys.GREY2 : tc[bgKey];
  const hoverBackgroundColor =
    colors === 'white on red' || colors === 'red on white'
      ? tc.redDark
      : colors === 'white on primary'
      ? tc.primaryDark
      : colors === 'white on amber'
      ? tc.amberDark
      : colors === 'white on GREY3C'
      ? tc.GREY4
      : bgKey == 'white'
      ? color
      : 'string' == typeof backgroundColor && backgroundColor.startsWith('#')
      ? lightenDarkenColor(backgroundColor, 20)
      : backgroundColor;
  const borderColor =
    colors == 'black on white' || buttonProps?.disabled
      ? theme.greys.GREY2
      : fgKey == 'white'
      ? lightenDarkenColor(backgroundColor as string, -10)
      : color;
  const fgColor =
    bgKey == 'white' && 'string' == typeof backgroundColor && backgroundColor.startsWith('#')
      ? lightenDarkenColor(backgroundColor, -10)
      : tc.white;

  buttonProps.sx = {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    backgroundColor,
    color,
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    height: square ? '80px' : '32px', // Square button is larger
    width: square ? '250px' : buttonWidth, // Square button has equal width and height
    margin: 0,
    padding: square ? '0px' : '5px 16px', // Adjust padding for square button
    display: 'flex', // Ensure content is centered
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: square ? '16px' : '14px',
    textWrap: square ? 'wrap' : 'nowrap',
    ...(pulsate && 'string' == typeof backgroundColor && backgroundColor.startsWith('#')
      ? {
          animation: 'button-pulsate 1.4s ease-in-out infinite',
          '@keyframes button-pulsate': {
            '0%, 100%': { backgroundColor },
            '50%': { backgroundColor: lightenDarkenColor(backgroundColor, 25) },
          },
        }
      : {}),
    '&:hover': {
      backgroundColor: hoverBackgroundColor,
      color: fgColor,
      '& svg path': {
        fill: theme.colors.white,
      },
    },
    ...buttonProps.sx,
  };

  return (
    <MUIButton ref={buttonRef} {...buttonProps}>
      {loading ? <CircularProgress color="inherit" size={16} /> : children}
    </MUIButton>
  );
};

export default Button;
